import { useCallback, useContext, useMemo, useState } from 'react';

import type { GridSortModel } from '@mui/x-data-grid';
import isEqual from 'lodash/isEqual';
import { useHistory } from 'react-router-dom';

import { FormDoc, User } from '@vyce/core/src/types';
import { useTable } from '@vyce/core/src/hooks/useTable';
import { GRID_PAGE_SIZE } from '@vyce/core/src/constants';
import { getFormsRequest, getFormsToAnswerRequest } from '@vyce/core/src/api/forms';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';

/** Matches `validSortFields` in Firebase `getForms` (functions/modules/forms/functions.js). */
const FORMS_API_SORT_FIELDS = ['createdAt', 'updatedAt', 'title', 'status'] as const;

const DEFAULT_FORMS_SORT_MODEL: GridSortModel = [{ field: 'updatedAt', sort: 'desc' }];

export function getFormsSortParams(model: GridSortModel) {
  const first = model[0];
  const field = first?.field;
  const isValid = field != null && (FORMS_API_SORT_FIELDS as readonly string[]).includes(field);
  if (!isValid) {
    return { sortBy: 'updatedAt' as const, sortOrder: 'desc' as const };
  }
  return {
    sortBy: field as (typeof FORMS_API_SORT_FIELDS)[number],
    sortOrder: first?.sort === 'asc' ? ('asc' as const) : ('desc' as const),
  };
}

export const useFormList = ({ user, companyId }: { user: User; companyId: string }) => {
  const [forms, setForms] = useState<FormDoc[]>([]);
  const [loading, setLoading] = useState(false);
  const history = useHistory();

  const {
    sortModel,
    offset,
    substring,
    total,
    setTotal,
    handlePageChange,
    setOffset,
    handleSearchChange,
    handleSortModelChange,
  } = useTable({ defaultSortModel: DEFAULT_FORMS_SORT_MODEL, initialSubstring: '' });
  const { handleServerError } = useContext(NotificationContext);

  const { sortBy, sortOrder } = useMemo(() => getFormsSortParams(sortModel), [sortModel]);

  const handleSortModelChangeWithReset = useCallback(
    (newModel: GridSortModel) => {
      if (isEqual(newModel, sortModel)) return;
      setOffset(0);
      handleSortModelChange(newModel);
      const params = new URLSearchParams(history.location.search);
      params.set('page', '0');
      const nextSearch = params.toString() ? `?${params.toString()}` : '?page=0';
      if (history.location.search !== nextSearch) {
        history.push({ search: nextSearch });
      }
    },
    [handleSortModelChange, setOffset, history, sortModel]
  );

  const getForms = useCallback(
    async (statusFilter?: 'archived', folderId?: string | null) => {
      try {
        setLoading(true);
        const res = await getFormsRequest({
          offset,
          limit: GRID_PAGE_SIZE,
          substring,
          companyId,
          userId: user.uuid,
          sortBy,
          sortOrder,
          status: statusFilter,
          folderId,
        });

        setForms(res.forms);
        setTotal(res.total);
        setLoading(false);
      } catch (e) {
        setLoading(false);
        handleServerError(e);
      }
    },
    [offset, substring, sortBy, sortOrder, companyId, user.uuid, handleServerError, setTotal]
  );

  const getFormsToAnswer = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getFormsToAnswerRequest({
        offset,
        limit: GRID_PAGE_SIZE,
        companyId,
        participant: { email: user?.email ?? '', phone: user?.phone ?? '' },
      });

      setForms(res.forms);
      setTotal(res.total);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      handleServerError(e);
    }
  }, [offset, companyId, user?.email, user?.phone, handleServerError, setTotal]);

  return {
    forms,
    loading,
    getForms,
    total,
    getFormsToAnswer,
    handlePageChange,
    offset,
    setOffset,
    handleSearchChange,
    sortModel,
    handleSortModelChange: handleSortModelChangeWithReset,
    substring,
  };
};
